import { computed, reactive, watch } from "vue"
import { useExploreStore } from "@/explore/ExploreStore"
import { useViewScopeStore } from "@/explore/ViewScopeStore"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { Gender, PersonName, ValueCount } from "@/rd/ResearchDataModel"
import { LoadMode } from "@/util/AsyncData"
import iterate from "iterare"

let instance: ReturnType<typeof createInstance> | undefined

export function useNameCounts() {
    return (instance ??= createInstance())
}

export interface NamePeriodCount {
    name: string
    period: number
    count: number
}

function buildCaseMap(counts: Map<string, number>) {
    const keyNames = new Map<string, string>() // lowercase -> preferred casing
    for (const n of counts.keys()) {
        const key = n.toLowerCase()
        if (keyNames.has(key)) {
            const existingName = keyNames.get(key)!
            const existingCount = counts.get(existingName)!
            const count = counts.get(n)!
            if (count > existingCount) {
                keyNames.set(key, n)
            }
        } else {
            keyNames.set(key, n)
        }
    }
    return keyNames
}

function createInstance() {
    const nameCounts = reactive(new Map<string, ValueCount[]>())
    const nameTimeData = reactive(new Map<string, NamePeriodCount[]>())

    const exploreStore = useExploreStore()
    const viewScopeStore = useViewScopeStore()
    const viewPersonStore = useViewPersonStore()

    const scopeInfoRef = computed(() => viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

    watch(scopeInfoRef, () => {
        nameCounts.clear()
        nameTimeData.clear()

        if (!scopeInfoRef.value)
            return

        const start = performance.now()

        const surnameCounts = new Map<string, number>()
        const maleGivenNameCounts = new Map<string, number>()
        const femaleGivenNameCounts = new Map<string, number>()

        // name -> period -> count
        const surnamePeriodCounts = new Map<string, Map<number, number>>()
        const maleGivenPeriodCounts = new Map<string, Map<number, number>>()
        const femaleGivenPeriodCounts = new Map<string, Map<number, number>>()

        for (const id of scopeInfoRef.value.personIds) {
            const vp = viewPersonStore.getLoadedPerson(id)
            const pn = new PersonName(vp?.displayProperties?.name)
            const birthYear = vp?.displayProperties.birthYear
            const period = birthYear !== undefined ? Math.floor(birthYear / 50) * 50 : undefined

            if (pn.surname) {
                addOrIncrement(surnameCounts, pn.surname)
                if (period !== undefined) {
                    addOrIncrementPeriod(surnamePeriodCounts, pn.surname, period)
                }
            }
            if (vp?.displayProperties.gender && pn.given) {
                const givenNames = pn.givenNames
                const counts = vp.displayProperties.gender == Gender.Male ? maleGivenNameCounts : femaleGivenNameCounts
                const periodCounts = vp.displayProperties.gender == Gender.Male ? maleGivenPeriodCounts : femaleGivenPeriodCounts
                for (const g of givenNames) {
                    addOrIncrement(counts, g)
                    if (period !== undefined) {
                        addOrIncrementPeriod(periodCounts, g, period)
                    }
                }
            }
        }

        const { sorted: surnames, caseMap: surnameCaseMap } = mergeAndSort(surnameCounts)
        const { sorted: maleGiven, caseMap: maleCaseMap } = mergeAndSort(maleGivenNameCounts)
        const { sorted: femaleGiven, caseMap: femaleCaseMap } = mergeAndSort(femaleGivenNameCounts)

        nameCounts.set('s', surnames)
        nameCounts.set('g:male', maleGiven)
        nameCounts.set('g:female', femaleGiven)

        nameTimeData.set('s', buildPeriodCounts(surnameCaseMap, surnamePeriodCounts))
        nameTimeData.set('g:male', buildPeriodCounts(maleCaseMap, maleGivenPeriodCounts))
        nameTimeData.set('g:female', buildPeriodCounts(femaleCaseMap, femaleGivenPeriodCounts))

        console.log(`NameCounts: Processed ${scopeInfoRef.value.personIds.size} persons in ${performance.now() - start} ms`)
    }, { immediate: true })

    function addOrIncrement(map: Map<string, number>, key: string) {
        map.set(key, (map.get(key) ?? 0) + 1)
    }

    function addOrIncrementPeriod(map: Map<string, Map<number, number>>, name: string, period: number) {
        let periodMap = map.get(name)
        if (!periodMap) {
            periodMap = new Map<number, number>()
            map.set(name, periodMap)
        }
        periodMap.set(period, (periodMap.get(period) ?? 0) + 1)
    }

    function mergeAndSort(counts: Map<string, number>) {
        const keyNames = buildCaseMap(counts)
        for (const n of counts.keys()) {
            const key = n.toLowerCase()
            const preferred = keyNames.get(key)!
            if (preferred !== n) {
                counts.set(preferred, counts.get(preferred)! + counts.get(n)!)
                counts.set(n, 0)
            }
        }

        // sort in place by count desc, then name
        const sortedNames = iterate(counts.keys())
            .filter(n => counts.get(n)! > 0)
            .map(n => ({ value: n, count: counts.get(n)! } as ValueCount))
            .toArray()
        sortedNames.sort((a, b) => -(a.count - b.count) || a.value.localeCompare(b.value))

        return { sorted: sortedNames, caseMap: keyNames }
    }

    function buildPeriodCounts(
        caseMap: Map<string, string>,
        periodCounts: Map<string, Map<number, number>>
    ): NamePeriodCount[] {
        // Merge case variants into preferred casing
        const merged = new Map<string, Map<number, number>>()
        for (const [name, periodMap] of periodCounts) {
            const preferred = caseMap.get(name.toLowerCase()) ?? name
            let mergedMap = merged.get(preferred)
            if (!mergedMap) {
                mergedMap = new Map<number, number>()
                merged.set(preferred, mergedMap)
            }
            for (const [period, count] of periodMap) {
                mergedMap.set(period, (mergedMap.get(period) ?? 0) + count)
            }
        }

        // Flatten to array
        const result: NamePeriodCount[] = []
        for (const [name, periodMap] of merged) {
            for (const [period, count] of periodMap) {
                result.push({ name, period, count })
            }
        }
        return result
    }

    return {
        nameCounts,
        nameTimeData,
    }
}
